import type { Schemas } from "@neio/contracts";
import { Ban, CircleCheck, FlaskConical, Play } from "lucide-react";
import type { Me } from "../../lib/me";
import { switchedOff } from "../../lib/http";
import { FeatureOff } from "../FeatureOff";
import { RoleButton } from "../RequireRole";
import { Empty, ErrorState, Loading } from "../States";
import { STARTABLE, type ModuleView, type Startable } from "./moduleFlow";

export type CatalogView = Schemas["CatalogView"];

const STATUS = {
  available: { label: "Available", tone: "ok", icon: <CircleCheck size={14} aria-hidden="true" /> },
  preview: { label: "Preview", tone: "review", icon: <FlaskConical size={14} aria-hidden="true" /> },
  blocked: { label: "Blocked", tone: "fail", icon: <Ban size={14} aria-hidden="true" /> },
} as const;

/** The request type a module's start form sends, when the screen has a form for it. */
export function startableType(module: ModuleView): Startable | null {
  const first = module.request_types[0];
  return (STARTABLE as readonly string[]).includes(first) ? (first as Startable) : null;
}

/** One catalog entry: what it does, whether it runs here, and why not when it does not. */
export function ModuleCard({
  module, me, onStart,
}: { module: ModuleView; me: Me | null | undefined; onStart: (requestType: Startable) => void }) {
  const status = STATUS[module.status];
  const type = startableType(module);
  return (
    <article className="card stack" style={{ gap: 10 }} aria-label={module.name}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <h3 style={{ fontSize: 17 }}>{module.name}</h3>
        <span className="row" style={{ gap: 6, flexShrink: 0 }}>
          <span className={`pill ${status.tone}`}><span className="row" style={{ gap: 4 }}>{status.icon}{status.label}</span></span>
          {!module.enabled && <span className="pill info">Off here</span>}
        </span>
      </div>
      <p className="muted" style={{ fontSize: 14 }}>{module.summary}</p>
      {/* A blocked module's reason repeats its blocker, which is shown once under Waiting on. */}
      {!module.enabled && module.status !== "blocked" && <p style={{ fontSize: 14 }} role="note">{module.reason}</p>}
      {module.enabled && module.status !== "available" && <p className="muted" style={{ fontSize: 13 }}>{module.reason}</p>}
      {module.blocked_on && module.status === "blocked" && (
        <p style={{ fontSize: 13 }}><strong>Waiting on: </strong>{module.blocked_on}</p>
      )}
      {module.needs.length > 0 && (
        <p className="muted" style={{ fontSize: 13 }}>Reads: {module.needs.join(", ")}</p>
      )}
      {module.enabled && type && (
        <div>
          <RoleButton role="engineer" me={me} onClick={() => onStart(type)} aria-label={`Start ${module.name}`}>
            <span className="row" style={{ gap: 6 }}><Play size={14} aria-hidden="true" />Start</span>
          </RoleButton>
        </div>
      )}
    </article>
  );
}

const ORDER = { available: 0, preview: 1, blocked: 2 } as const;

/** The catalog with its loading, error, empty and switched-off states. */
export function CatalogBody({
  loading, error, data, me, onStart,
}: {
  loading: boolean;
  error: unknown;
  data: CatalogView | undefined;
  me: Me | null | undefined;
  onStart: (requestType: Startable) => void;
}) {
  if (loading) return <Loading what="Loading the module catalog" />;
  if (error) {
    const off = switchedOff(error, [404]);
    return off !== null ? <FeatureOff feature="The module service" reason={off} /> : <ErrorState error={error} />;
  }
  if (!data || data.modules.length === 0) return <Empty title="No modules are listed">This deployment reports no modules.</Empty>;
  // Runs here first, then preview, then blocked; within each, the catalog's own order.
  const sorted = [...data.modules].sort(
    (a, b) => Number(b.enabled) - Number(a.enabled) || ORDER[a.status] - ORDER[b.status],
  );
  return (
    <>
      <p className="muted" style={{ marginBottom: 16 }}>
        {data.enabled} of {data.modules.length} modules run on this environment.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {sorted.map((m) => <ModuleCard key={m.key} module={m} me={me} onStart={onStart} />)}
      </div>
    </>
  );
}
